import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { capitalize } from 'lodash'

import QuestionsControls from './QuestionsControls'
import Slide from './Slide'
import SlidePreview from './SlidePreview'
import Typography from '../../UniformComponents/Typography'
import FilterModal from './modals/Filter'
import SlideSelectModal from './modals/SlideSelectModal'
import VerbatimSettingsModal from './modals/VerbatimSettingsModal'
import PresentationSettingsModal from './modals/PresentationSettingsModal'
import SlideSettingsModal from './modals/SlideSettingsModal'
import ChartSettingsModal from './modals/ChartSettingsModal'
import DerivedChartModal from './modals/DerivedChartModal'
import InheritedChartModal from './modals/InheritedChartModal'
import LMXSettingsModal from './modals/LMXSettingsModal'
import CrossbreakSettingsModal from './modals/CrossbreakSettingsModal'
import DownloadPresentationModal from './modals/DownloadPresentationModal'
import DownloadWarningModal from './modals/DownloadWarningModal'
import VerbatimFilterWarningModal from './modals/VerbatimFilterWarningModal'
import SaveStatusPill from './SaveStatusPill'

import './ppbuilder.scss'
import Button from '../../UniformComponents/Button'
import SectionTitleModal from './modals/SectionTitleModal'
import InsertTemplateModal from './modals/InsertTemplateModal'
import TemplateSettingsModal from './modals/TemplateSettingsModal'
import AddSectionModal from './modals/AddSectionModal'

const PowerpointBuilder = ({ store }) => {
    const {
        handleOpenSlideSelectModal, onQuestionDragEnd, currentSlide,
        currentSection, eventTitle, requestDownload, handleOpenGlobalSettingsModal, activeSection,
        slideCount, presentation, currentSlideIndex,
        undo, redo, canUndo, canRedo, saveStatus, saveStatusLabel,
    } = store

    return (
        <div>
            <div style={{ width: '100%', paddingInline: 24, backgroundColor: 'white', borderBottom: '1px solid grey', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 72 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <Typography type="h2">{i18n.t('presentationbuilder')}</Typography>

                    <Typography type="tiny">{presentation.title} - {eventTitle}</Typography>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div
                        onClick={canUndo ? undo : undefined}
                        className={canUndo ? 'pp-slide-change-button' : 'pp-slide-change-button-disabled'}
                    >
                        <i className="far fa-arrow-rotate-left" />
                    </div>

                    <div
                        onClick={canRedo ? redo : undefined}
                        className={canRedo ? 'pp-slide-change-button' : 'pp-slide-change-button-disabled'}
                    >
                        <i className="far fa-arrow-rotate-right" />
                    </div>

                    <div style={{ width: 2, height: 32, backgroundColor: '#CADAE1' }} />

                    {/* <Button
                        icon="far fa-save"
                        label="Dev Save"
                        onClick={store.handleSavePresentation}
                        cssClass="regular primary"
                    /> */}

                    {store.isTemplate ? (
                        <Fragment>
                            <Button
                                icon="far fa-gear"
                                label={i18n.t('templates.settings.title')}
                                onClick={store.openTemplateSettingsModal}
                                cssClass="regular tertiary"
                            />

                            <Button
                                icon="far fa-save"
                                label={i18n.t('templates.saveTemplate')}
                                onClick={store.saveTemplate}
                                cssClass="regular primary"
                                disabled={store.saving}
                            />
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Button
                                icon="far fa-gear"
                                label="Presentation settings"
                                onClick={handleOpenGlobalSettingsModal}
                                cssClass="regular tertiary"
                            />

                            <Button
                                icon="far fa-download"
                                label="Download presentation"
                                onClick={requestDownload}
                                cssClass="regular secondary"
                            />
                        </Fragment>
                    )}
                </div>
            </div>

            <div className="pp-container">
                <DragDropContext onDragEnd={onQuestionDragEnd}>
                    <div className="pp-left-container">
                        <QuestionsControls store={store} />
                    </div>

                    <div className="pp-slide-container">
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid grey', width: '100%', paddingInline: 24, height: 100, alignItems: 'center', backgroundColor: 'white' }}>
                            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                                <div onClick={() => store.handleKeyboardListener(-1)} className={`${!currentSection && !currentSlide ? 'pp-slide-change-button-disabled' : 'pp-slide-change-button'}`}>
                                    <i className="far fa-chevron-left" />
                                </div>

                                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                    <Typography type="b">{activeSection?.title || 'Section'}</Typography>
                                    <Typography type="tiny" color="grey">•</Typography>
                                    <Typography color="grey">{`slide ${currentSlideIndex} of ${slideCount}`}</Typography>
                                </div>

                                <div onClick={() => store.handleKeyboardListener(1)} className={`${(activeSection?.slide?.length - 1 === currentSlide && currentSection === presentation.section.length - 1) ? 'pp-slide-change-button-disabled' : 'pp-slide-change-button'}`}>
                                    <i className="far fa-chevron-right" />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <SaveStatusPill status={saveStatus} label={saveStatusLabel} />

                                <div style={{ width: 2, height: 20, backgroundColor: '#CADAE1' }} />

                                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                    <Typography type="tiny" color="grey">{store.activeSlideRespondentTypes}</Typography>
                                    <Typography type="tiny" color="grey">•</Typography>
                                    <Typography type="tiny" color="grey">{capitalize(store.activeSlide?.template)}</Typography>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, width: '100%', height: '100%' }}>
                            <Droppable
                                key={0}
                                droppableId="add-above"
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        <div className={`pp-add-slide-container ${!currentSection && !currentSlide ? 'pp-add-slide-container-disabled' : ''}`} onClick={() => handleOpenSlideSelectModal('above')}>
                                            <div className={`${!currentSection && !currentSlide ? 'pp-disable-add-line' : 'pp-add-slide-line'}`} />
                                            <div className={`${!currentSection && !currentSlide ? 'pp-disabled-add' : 'pp-add-slide'}`}>
                                                <i className="far fa-plus" />
                                                <Typography type="b">Add slide above</Typography>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Droppable>

                            <div style={{ width: '100%', paddingInline: 40, position: 'relative' }}>
                                {store.templateEventLoading && (
                                    <div style={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, backgroundColor: 'rgba(255, 255, 255, 0.75)' }}>
                                        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 32, color: '#715AF8' }} />
                                        <Typography type="tiny" color="grey">{i18n.t('templates.loadingEventData')}</Typography>
                                    </div>
                                )}

                                <Slide store={store} />
                            </div>

                            <Droppable
                                key={0}
                                droppableId="add-below"
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        <div className="pp-add-slide-container" onClick={() => handleOpenSlideSelectModal('below')}>
                                            <div className="pp-add-slide-line" />

                                            <div className="pp-add-slide">
                                                <i className="far fa-plus" />

                                                <Typography type="b">Add slide below</Typography>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    </div>
                </DragDropContext>

                <SlidePreview store={store} />
            </div>

            <SlideSelectModal store={store} />

            <VerbatimSettingsModal store={store} />

            <PresentationSettingsModal store={store} />

            <SlideSettingsModal store={store} />

            <ChartSettingsModal store={store} />

            <DerivedChartModal store={store} />

            <InheritedChartModal store={store} />

            <LMXSettingsModal store={store} />

            <CrossbreakSettingsModal store={store} />

            <SectionTitleModal store={store} />

            <DownloadPresentationModal store={store} />

            <DownloadWarningModal store={store} />

            <FilterModal store={store} />

            <VerbatimFilterWarningModal store={store} />

            <InsertTemplateModal store={store} />

            <TemplateSettingsModal store={store} />

            <AddSectionModal store={store} />
        </div>
    )
}

export default observer(PowerpointBuilder)
